// src/components/gestionEvento/Ajustes/Competidores/Inscripciones/sidebar/PruebasSection.jsx
import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  ListItemText,
  IconButton,
  TextField,
  Button,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

// --- Helpers de normalización ---
const toId = (v) => (v == null ? '' : String(v));

function getRowsFromValue(value, keyIds, keyData) {
  // Genera filas [{id, marca}] a partir de pruebasIds / pruebasData
  const ids = Array.isArray(value?.[keyIds]) ? value[keyIds].map(toId) : [];
  const dataMap = {};
  if (Array.isArray(value?.[keyData])) {
    value[keyData].forEach((d) => {
      const pid = toId(d?.pruebaId ?? d?.id);
      if (pid) dataMap[pid] = { marca: d?.marca ?? '' };
    });
  }
  return ids.map((id) => ({ id, marca: dataMap[id]?.marca ?? '' }));
}

function buildIdsAndData(rows, allPruebas) {
  // rows: [{id, marca}] -> { ids: string[], data: {pruebaId, prueba, eventCode, testType, marca}[] }
  const ids = [];
  const data = [];
  const seen = new Set();
  rows.forEach((r) => {
    const id = toId(r.id);
    if (!id || seen.has(id)) return;
    const p = allPruebas.find((x) => toId(x.id) === id);
    if (!p) return;
    seen.add(id);
    ids.push(id);
    data.push({
      pruebaId: p.id,
      prueba: p.nombre,
      eventCode: p.eventCode || '',
      testType: p.testType || '',
      marca: r.marca ?? '',
    });
  });
  return { ids, data };
}

// --- Componente ---
function PruebasSection({ value, onChange, readOnly, competition, pruebas }) {
  const rama = value?.rama || '';
  const categoriaId = value?.categoria?.id || null;

  const allPruebas = Array.isArray(pruebas) ? pruebas : (competition?.pruebas || []);

  // Filtrar por categoría / rama
  const pruebasDisponibles = useMemo(() => {
    if (!Array.isArray(allPruebas) || allPruebas.length === 0) return [];
    if (!categoriaId) return allPruebas;

    const isFem = rama === 'Femenil';
    const isVar = rama === 'Varonil';

    return allPruebas.filter((p) => {
      const inFem = Array.isArray(p.categoriaIdsFemenil) && p.categoriaIdsFemenil.includes(categoriaId);
      const inVar = Array.isArray(p.categoriaIdsVaronil) && p.categoriaIdsVaronil.includes(categoriaId);
      if (isFem) return inFem;
      if (isVar) return inVar;
      return inFem || inVar; // Mixto u otro
    });
  }, [allPruebas, categoriaId, rama]);

  const individuales = useMemo(
    () => pruebasDisponibles.filter((p) => (p.testType || '').toLowerCase() !== 'relay'),
    [pruebasDisponibles]
  );
  const relevos = useMemo(
    () => pruebasDisponibles.filter((p) => (p.testType || '').toLowerCase() === 'relay'),
    [pruebasDisponibles]
  );

  // Obtén filas actuales desde value
  const indRows = getRowsFromValue(value, 'pruebasIds', 'pruebasData');
  const relRows = getRowsFromValue(value, 'relevosIds', 'relevosData');

  // Memoized values for adding rows (must be before conditional return)
  const canAddInd = useMemo(() => {
    const selected = new Set(indRows.map((r) => toId(r.id)));
    return individuales.some((p) => !selected.has(toId(p.id)));
  }, [indRows, individuales]);

  const canAddRel = useMemo(() => {
    const selected = new Set(relRows.map((r) => toId(r.id)));
    return relevos.some((p) => !selected.has(toId(p.id)));
  }, [relRows, relevos]);

  // Si cambian rama/categoría, limpia filas que ya no existan
  useEffect(() => {
    const validInd = indRows.filter((r) => individuales.some((p) => toId(p.id) === toId(r.id)));
    const validRel = relRows.filter((r) => relevos.some((p) => toId(p.id) === toId(r.id)));

    if (validInd.length !== indRows.length || validRel.length !== relRows.length) {
      const newInd = buildIdsAndData(validInd, allPruebas);
      const newRel = buildIdsAndData(validRel, allPruebas);
      onChange?.((prev) => ({
        ...prev,
        pruebasIds: newInd.ids,
        pruebasData: newInd.data,
        relevosIds: newRel.ids,
        relevosData: newRel.data,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rama, categoriaId, individuales.length, relevos.length]);

  if (readOnly) {
    const chips = [
      ...(value?.pruebasData || []).map((d) => (d?.marca ? `${d.prueba} (${d.marca})` : d?.prueba)).filter(Boolean),
      ...(value?.relevosData || []).map((d) => (d?.marca ? `${d.prueba} (${d.marca})` : d?.prueba)).filter(Boolean),
    ];
    return (
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">Pruebas</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {chips.length
            ? chips.map((n) => <Chip key={n} label={n} size="small" />)
            : <Typography variant="body2">—</Typography>}
        </Box>
      </Stack>
    );
  }

  // Funciones de edición por filas
  const updateRows = (kind, rows) => {
    const list = kind === 'ind' ? individuales : relevos;
    const built = buildIdsAndData(rows, list.length ? allPruebas : allPruebas);
    onChange?.((prev) => ({
      ...prev,
      ...(kind === 'ind'
        ? { pruebasIds: built.ids, pruebasData: built.data }
        : { relevosIds: built.ids, relevosData: built.data }),
    }));
  };

  const addRow = (kind) => {
    const rows = kind === 'ind' ? indRows : relRows;
    const list = kind === 'ind' ? individuales : relevos;
    const selectedIds = new Set(rows.map((r) => toId(r.id)));
    const firstFree = list.find((p) => !selectedIds.has(toId(p.id)));
    if (!firstFree) return;
    const newRows = [...rows, { id: toId(firstFree.id), marca: '' }];
    updateRows(kind, newRows);
  };

  const removeRow = (kind, idx) => {
    const rows = (kind === 'ind' ? indRows : relRows).filter((_, i) => i !== idx);
    updateRows(kind, rows);
  };

  const changeRowId = (kind, idx, newId) => {
    const rows = kind === 'ind' ? indRows : relRows;
    const newRows = rows.map((r, i) => (i === idx ? { ...r, id: toId(newId) } : r));
    updateRows(kind, newRows);
  };

  const changeRowMarca = (kind, idx, marca) => {
    const rows = kind === 'ind' ? indRows : relRows;
    const newRows = rows.map((r, i) => (i === idx ? { ...r, marca } : r));
    updateRows(kind, newRows);
  };

  // Opciones disponibles por fila (evita duplicados)
  const availableForRow = (kind, idx) => {
    const list = kind === 'ind' ? individuales : relevos;
    const rows = kind === 'ind' ? indRows : relRows;
    const selected = rows.map((r) => toId(r.id));
    const current = toId(rows[idx]?.id);
    const selectedExceptCurrent = new Set(selected.filter((id, i) => i !== idx));
    return list.filter((p) => !selectedExceptCurrent.has(toId(p.id)));
  };

  return (
    <Stack spacing={2}>
      {/* INDIVIDUALES */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">Pruebas individuales</Typography>
          <Button
            startIcon={<AddIcon />}
            size="small"
            disabled={!canAddInd}
            onClick={() => addRow('ind')}
          >
            Agregar
          </Button>
        </Stack>

        {indRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Sin pruebas individuales seleccionadas.</Typography>
        ) : (
          <Stack spacing={1}>
            {indRows.map((row, idx) => {
              const options = availableForRow('ind', idx);
              const hasCurrent = options.some((p) => toId(p.id) === toId(row.id));
              const selectValue = hasCurrent ? toId(row.id) : '';
              return (
                <Stack key={`ind-${idx}`} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                  <Select
                    size="small"
                    value={selectValue}
                    onChange={(e) => changeRowId('ind', idx, e.target.value)}
                    displayEmpty
                    sx={{ minWidth: 250, flexShrink: 0 }}
                  >
                    <MenuItem value=""><em>—</em></MenuItem>
                    {options.map((p) => (
                      <MenuItem key={toId(p.id)} value={toId(p.id)}>
                        <Checkbox checked={toId(p.id) === toId(row.id)} />
                        <ListItemText primary={p.nombre} secondary={p.eventCode} />
                      </MenuItem>
                    ))}
                  </Select>

                  <TextField
                    size="small"
                    label="Marca de siembra"
                    value={row.marca ?? ''}
                    onChange={(e) => changeRowMarca('ind', idx, e.target.value)}
                    placeholder="Ej. 12.34, 5.80m, 1:58.20"
                    sx={{ flex: 1, minWidth: 180 }}
                  />

                  <IconButton aria-label="quitar" onClick={() => removeRow('ind', idx)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>

      <Divider />

      {/* RELEVOS */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">Relevos</Typography>
          <Button
            startIcon={<AddIcon />}
            size="small"
            disabled={!canAddRel}
            onClick={() => addRow('rel')}
          >
            Agregar
          </Button>
        </Stack>

        {relRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Sin relevos seleccionados.</Typography>
        ) : (
          <Stack spacing={1}>
            {relRows.map((row, idx) => {
              const options = availableForRow('rel', idx);
              const hasCurrent = options.some((p) => toId(p.id) === toId(row.id));
              const selectValue = hasCurrent ? toId(row.id) : '';
              return (
                <Stack key={`rel-${idx}`} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                  <Select
                    size="small"
                    value={selectValue}
                    onChange={(e) => changeRowId('rel', idx, e.target.value)}
                    displayEmpty
                    sx={{ minWidth: 250, flexShrink: 0 }}
                  >
                    <MenuItem value=""><em>—</em></MenuItem>
                    {options.map((p) => (
                      <MenuItem key={toId(p.id)} value={toId(p.id)}>
                        <Checkbox checked={toId(p.id) === toId(row.id)} />
                        <ListItemText primary={p.nombre} secondary={p.eventCode} />
                      </MenuItem>
                    ))}
                  </Select>

                  <TextField
                    size="small"
                    label="Marca de siembra"
                    value={row.marca ?? ''}
                    onChange={(e) => changeRowMarca('rel', idx, e.target.value)}
                    placeholder="Ej. 4x100 45.20"
                    sx={{ flex: 1, minWidth: 180 }}
                  />

                  <IconButton aria-label="quitar" onClick={() => removeRow('rel', idx)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

PruebasSection.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  competition: PropTypes.object,
  pruebas: PropTypes.array,
};

export default PruebasSection;
